import { isCorrelationId } from '../../errors/correlation.js';
import type { TxEngineConfig } from '../config.js';
import {
  hashDiagnosticEntity,
  type InternalSubmissionResult,
  type SubmissionDiagnostics,
  TX_LIFECYCLE_EVENT,
  TX_LIFECYCLE_SCHEMA_VERSION,
} from '../diagnostics/lifecycle.js';
import {
  lookupErrorMessageKey,
  safeRecoveryActionType,
  safeTxErrorCode,
  safeTxStage,
  TxErrorCode,
} from '../errors/errors.js';
import type { SubmissionAttemptContext, SubmissionOptions, TxPath } from '../types.js';

export type RouteSelectionReason =
  | 'external_wallet'
  | 'embedded_default'
  | 'forced_relay'
  | 'additional_presigner'
  | 'local_rpc'
  | 'idempotent_ata_create';

export interface RecoveryContext {
  reason: 'not_configured' | 'mid_flight_quota_exceeded' | 'turnkey_failed';
  providerErrorCode?: string;
  providerActivityIdHash?: string;
  turnkeyDiagnostics?: SubmissionDiagnostics;
}

export interface TerminalLifecycleInput {
  traceId: string;
  startMs: number;
  initialPath: TxPath;
  finalPath: TxPath;
  selectionReason: RouteSelectionReason;
  result: InternalSubmissionResult;
  options: SubmissionOptions;
  network: string;
  sampledTrace: boolean;
  recovery?: RecoveryContext;
  attemptContext?: SubmissionAttemptContext;
}

export function logBlockedLifecycle(
  cfg: TxEngineConfig,
  traceId: string,
  options: SubmissionOptions,
  startMs: number,
  attemptContext?: SubmissionAttemptContext,
): void {
  cfg.logger.warn(TX_LIFECYCLE_EVENT, {
    eventName: TX_LIFECYCLE_EVENT,
    schemaVersion: TX_LIFECYCLE_SCHEMA_VERSION,
    traceId,
    initialTxPath: 'not_selected',
    txPath: 'not_selected',
    phase: 'terminal',
    outcome: 'blocked',
    instructionName: options.instructionName,
    walletType: options.activeWallet.type,
    walletHash: hashDiagnosticEntity(options.activeWallet.address),
    network: cfg.resolveConnectionConfig().network,
    durationMs: Date.now() - startMs,
    errorCode: TxErrorCode.TX_UNKNOWN,
    errorStage: 'preflight',
    blockedReason: 'impersonation_active',
    ...attemptContextFields(attemptContext),
  });
}

export function logTerminalLifecycle(cfg: TxEngineConfig, input: TerminalLifecycleInput): void {
  const { logger } = cfg;
  const durationMs = Date.now() - input.startMs;
  const details = input.result.ok ? {} : readErrorDetails(input.result.error);
  const diagnostics = sanitizeSubmissionDiagnostics(
    input.result.diagnostics ?? diagnosticsFromDetails(details),
  );
  const cancelled = !input.result.ok && details.cancelled === true;
  const recovered =
    input.result.ok &&
    (input.recovery !== undefined || input.attemptContext?.retryAttempt !== undefined);
  const degraded =
    input.selectionReason === 'idempotent_ata_create' || hasDegradedDiagnostics(diagnostics);
  const outcome = input.result.ok
    ? recovered
      ? 'recovered'
      : 'succeeded'
    : cancelled
      ? 'cancelled'
      : 'failed';
  const context = {
    eventName: TX_LIFECYCLE_EVENT,
    schemaVersion: TX_LIFECYCLE_SCHEMA_VERSION,
    traceId: input.traceId,
    initialTxPath: input.initialPath,
    txPath: input.finalPath,
    phase: 'terminal',
    outcome,
    selectionReason: input.selectionReason,
    instructionName: input.options.instructionName,
    walletType: input.options.activeWallet.type,
    walletHash: hashDiagnosticEntity(input.options.activeWallet.address),
    network: input.network,
    durationMs,
    ...(input.result.ok
      ? { signatureHash: hashDiagnosticEntity(input.result.signature) }
      : {
          errorCode: safeTxErrorCode(input.result.error.code),
          errorClass: readErrorClass(details.errorClass) ?? 'TxError',
          errorStage: safeTxStage(input.result.error.stage),
          recoveryAction: safeRecoveryActionType(input.result.error.recoveryAction.type),
          ...(readMorfiError(input.result.error.morfiError) === undefined
            ? {}
            : { morfiError: readMorfiError(input.result.error.morfiError) }),
        }),
    ...(input.recovery === undefined
      ? {}
      : {
          failoverReason: input.recovery.reason,
          ...(input.recovery.providerErrorCode === undefined
            ? {}
            : { providerErrorCode: input.recovery.providerErrorCode }),
          ...(input.recovery.providerActivityIdHash === undefined
            ? {}
            : { providerActivityIdHash: input.recovery.providerActivityIdHash }),
        }),
    ...attemptContextFields(input.attemptContext),
    ...turnkeyRecoveryFields(input.recovery?.turnkeyDiagnostics),
    ...diagnostics,
    ...failureContractFields(details),
    ...signingFailureFields(details, input.result.ok ? undefined : input.result.error.stage),
    ...buildProbeFields(details),
  } as const;

  if (!input.result.ok && !cancelled) {
    logger.error(TX_LIFECYCLE_EVENT, context);
  } else if (cancelled || recovered || degraded) {
    logger.info(TX_LIFECYCLE_EVENT, context);
  } else if (input.sampledTrace) {
    logger.info(TX_LIFECYCLE_EVENT, context);
  }
}

function attemptContextFields(
  context: SubmissionAttemptContext | undefined,
): Record<string, number | string> {
  if (context === undefined) return {};
  return {
    workflow: context.workflow,
    workflowTraceId: context.workflowTraceId,
    ...(context.retryAttempt === undefined
      ? {}
      : { initialTraceId: context.initialTraceId, retryAttempt: context.retryAttempt }),
  };
}

function hasDegradedDiagnostics(diagnostics: SubmissionDiagnostics | undefined): boolean {
  if (diagnostics === undefined) return false;
  return (
    diagnostics.simulationOutcome !== undefined ||
    diagnostics.wsSubscriptionFallback === true ||
    (diagnostics.pollingTransportFailures ?? 0) > 0 ||
    (diagnostics.blockHeightTransportFailures ?? 0) > 0 ||
    (diagnostics.expiryStatusLookups ?? 0) > 0 ||
    (diagnostics.rateLimitResponses ?? 0) > 0 ||
    (diagnostics.transportFailures ?? 0) > 0
  );
}

export function readErrorDetails(err: unknown): Record<string, unknown> {
  if (err === null || typeof err !== 'object' || !('details' in err)) return {};
  const details = (err as { details?: unknown }).details;
  return details !== null && typeof details === 'object'
    ? (details as Record<string, unknown>)
    : {};
}

export function diagnosticsFromDetails(
  details: Record<string, unknown> | undefined,
): SubmissionDiagnostics | undefined {
  if (details === undefined) return undefined;
  const diagnostics: SubmissionDiagnostics = {};
  const clientCorrelationId = readCorrelationId(details.clientCorrelationId);
  if (clientCorrelationId !== undefined) diagnostics.clientCorrelationId = clientCorrelationId;
  const backendRequestId = readRequestId(details.backendRequestId);
  if (backendRequestId !== undefined) diagnostics.backendRequestId = backendRequestId;
  const backendCorrelationId = readCorrelationId(details.backendCorrelationId);
  if (backendCorrelationId !== undefined) {
    diagnostics.backendCorrelationId = backendCorrelationId;
  }
  const providerActivityIdHash = readEntityHash(details.providerActivityIdHash);
  if (providerActivityIdHash !== undefined) {
    diagnostics.providerActivityIdHash = providerActivityIdHash;
  }
  for (const key of [
    'buildDurationMs',
    'submitDurationMs',
    'confirmationDurationMs',
    'totalStatusRequests',
    'successfulStatusResponses',
    'rateLimitResponses',
    'transportFailures',
    'pollingTransportFailures',
    'expiryStatusLookups',
    'blockHeightTransportFailures',
  ] as const) {
    const value = readNumber(details[key]);
    if (value !== undefined) diagnostics[key] = value;
  }
  if (typeof details.wsSubscriptionFallback === 'boolean') {
    diagnostics.wsSubscriptionFallback = details.wsSubscriptionFallback;
  }
  if (details.simulationOutcome === 'program_error' || details.simulationOutcome === 'rpc_error') {
    diagnostics.simulationOutcome = details.simulationOutcome;
  }
  return Object.keys(diagnostics).length === 0 ? undefined : diagnostics;
}

export function readEntityHash(value: unknown): string | undefined {
  return typeof value === 'string' && /^e_[0-9a-f]{8}$/u.test(value) ? value : undefined;
}

export function readProviderErrorCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return [
    'NO_SESSION_FOUND',
    'PERMISSION_DENIED',
    'RATE_LIMIT_EXCEEDED',
    'REQUEST_INVALID',
    'SESSION_EXPIRED',
    'SIGNING_QUOTA_EXCEEDED',
  ].includes(value)
    ? value
    : undefined;
}

function sanitizeSubmissionDiagnostics(
  input: SubmissionDiagnostics | undefined,
): SubmissionDiagnostics | undefined {
  if (input === undefined) return undefined;
  const safe: SubmissionDiagnostics =
    diagnosticsFromDetails(input as unknown as Record<string, unknown>) ?? {};
  if (typeof input.wsSubscriptionFallback === 'boolean') {
    safe.wsSubscriptionFallback = input.wsSubscriptionFallback;
  }
  if (input.simulationOutcome === 'program_error' || input.simulationOutcome === 'rpc_error') {
    safe.simulationOutcome = input.simulationOutcome;
  }
  return Object.keys(safe).length === 0 ? undefined : safe;
}

function failureContractFields(
  details: Record<string, unknown>,
): Record<string, boolean | number | string> {
  const fields: Record<string, boolean | number | string> = {};
  const backendErrorCode = readRelayBackendErrorCode(details.backendErrorCode);
  if (backendErrorCode !== undefined) fields.backendErrorCode = backendErrorCode;
  const backendNumericId = readBackendNumericId(details.backendNumericId);
  if (backendNumericId !== undefined) fields.backendNumericId = backendNumericId;
  const httpStatus = readHttpStatus(details.httpStatus);
  if (httpStatus !== undefined) fields.httpStatus = httpStatus;
  const responseContract = readRelayResponseContract(details.responseContract);
  if (responseContract !== undefined) fields.responseContract = responseContract;
  const responseContentType = classifyResponseContentType(details.responseContentType);
  if (responseContentType !== undefined) fields.responseContentType = responseContentType;
  if (typeof details.hasBackendDetails === 'boolean') {
    fields.hasBackendDetails = details.hasBackendDetails;
  }
  return fields;
}

export function signingFailureFields(
  details: Record<string, unknown>,
  stage: string | undefined,
): Record<string, number | string> {
  if (stage !== 'signing') return {};
  const fields: Record<string, number | string> = {};
  const safe = (value: unknown): value is string =>
    typeof value === 'string' && /^[A-Za-z0-9_.-]{1,64}$/.test(value);
  if (safe(details.errorName)) fields.signingErrorName = details.errorName;
  if (safe(details.errorCode)) fields.signingErrorCode = details.errorCode;
  if (
    typeof details.signingFailureCategory === 'string' &&
    ['transport', 'rejected', 'cancelled', 'unknown', 'timeout', 'session'].includes(
      details.signingFailureCategory,
    )
  ) {
    fields.signingFailureCategory = details.signingFailureCategory;
  }
  const status = details.statusCode;
  if (typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599) {
    fields.signingStatusCode = status;
  }
  return fields;
}

function buildProbeFields(details: Record<string, unknown>): Record<string, boolean | string> {
  const fields: Record<string, boolean | string> = {};
  for (const key of ['errorCauseClass', 'bufferProbeErrorClass'] as const) {
    const value = readErrorClass(details[key]);
    if (value !== undefined) fields[key] = value;
  }
  for (const key of [
    'bufferAvailable',
    'bufferAllocationSucceeded',
    'bufferWriteUInt32LEAvailable',
    'bufferWriteBigUInt64LEAvailable',
  ] as const) {
    if (typeof details[key] === 'boolean') fields[key] = details[key];
  }
  if (details.bufferConstructor === 'Buffer') fields.bufferConstructor = 'Buffer';
  if (
    details.bufferProbeStatus === 'unavailable' ||
    details.bufferProbeStatus === 'allocation_failed' ||
    details.bufferProbeStatus === 'probe_failed' ||
    details.bufferProbeStatus === 'ok'
  ) {
    fields.bufferProbeStatus = details.bufferProbeStatus;
  }
  return fields;
}

function turnkeyRecoveryFields(
  diagnostics: SubmissionDiagnostics | undefined,
): Record<string, boolean | number> {
  if (diagnostics === undefined) return {};
  const fields: Record<string, boolean | number> = {};
  const mappings = [
    ['buildDurationMs', 'turnkeyBuildDurationMs'],
    ['submitDurationMs', 'turnkeySubmitDurationMs'],
    ['confirmationDurationMs', 'turnkeyConfirmationDurationMs'],
    ['totalStatusRequests', 'turnkeyTotalStatusRequests'],
    ['successfulStatusResponses', 'turnkeySuccessfulStatusResponses'],
    ['rateLimitResponses', 'turnkeyRateLimitResponses'],
    ['transportFailures', 'turnkeyTransportFailures'],
    ['pollingTransportFailures', 'turnkeyPollingTransportFailures'],
    ['expiryStatusLookups', 'turnkeyExpiryStatusLookups'],
    ['blockHeightTransportFailures', 'turnkeyBlockHeightTransportFailures'],
  ] as const;
  for (const [source, target] of mappings) {
    const value = diagnostics[source];
    if (value !== undefined) fields[target] = value;
  }
  if (diagnostics.wsSubscriptionFallback !== undefined) {
    fields.turnkeyWsSubscriptionFallback = diagnostics.wsSubscriptionFallback;
  }
  return fields;
}

function readCorrelationId(value: unknown): string | undefined {
  if (isCorrelationId(value) && value[0] !== undefined && value[0] <= '7') return value;
  return readRequestId(value);
}

function readRequestId(value: unknown): string | undefined {
  if (
    typeof value !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value)
  ) {
    return undefined;
  }
  return value;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

function readErrorClass(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return [
    'AggregateError',
    'AbortError',
    'Error',
    'EvalError',
    'non_error',
    'RangeError',
    'ReferenceError',
    'RelaySubmissionError',
    'SyntaxError',
    'TurnkeySubmissionFailure',
    'TxError',
    'TypeError',
    'URIError',
    'unknown',
  ].includes(value)
    ? value
    : undefined;
}

function readMorfiError(value: unknown): string | undefined {
  return typeof value === 'string' && lookupErrorMessageKey(value) !== undefined
    ? value
    : undefined;
}

function readRelayBackendErrorCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return [
    'RELAY_FEE_PAYER_LOW',
    'RELAY_INVALID_TRANSACTION',
    'RELAY_RATE_LIMITED',
    'RELAY_SIMULATION_FAILED',
    'RELAY_SUBMISSION_FAILED',
  ].includes(value)
    ? value
    : undefined;
}

function readBackendNumericId(value: unknown): number | undefined {
  const numericId = readNumber(value);
  return numericId !== undefined && [18001, 18002, 18003, 18004, 18005].includes(numericId)
    ? numericId
    : undefined;
}

function readHttpStatus(value: unknown): number | undefined {
  const status = readNumber(value);
  return status !== undefined && status >= 100 && status <= 599 ? status : undefined;
}

function readRelayResponseContract(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return [
    'canonical_error',
    'incomplete_success',
    'invalid_error_envelope',
    'legacy_error',
    'malformed_json',
    'network_error',
    'unexpected_response',
  ].includes(value)
    ? value
    : undefined;
}

function classifyResponseContentType(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (value === 'application/json' || value.endsWith('+json')) return 'json';
  if (value === 'text/plain') return 'text';
  if (value === 'text/html') return 'html';
  return 'other';
}
